'use client';

import Link from 'next/link';
import {
  JSX,
  MouseEvent,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';

// Icons & Styles
import { AtIcon } from '../at-icon';
import { EIcons } from '../at-icon/icons';
import './at-clickable.css';
import atClickable from './at-clickable.variants';

// Contexts & Hooks
import { useLeadGenModal } from '@/context/leadGenModalContext';
import { useLocation } from '@/context/locationContext';
import { LoginModalContext } from '@/context/loginModalContext';
import {
  useSetLocationModal,
  ESetLocationType,
} from '@/context/setLocationContext';

// Utils
import { getRegionUrlById } from '@/components/templates/tm-home-plan-detail/utils/locationMapper';
import { openChat } from '@/components/integrations/GenesysLoader';
import {
  sendGTMClickEvent,
  sendGTMNavigationEvent,
} from '@/components/helpers/analytics/gtm';
import { getDesignStudioCalendlyLink } from '@/lib/contentstack-sdk';

// Types
import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
  TClickableIconPosition,
  TClickableVariants,
} from './at-clickable.types';
import { useInitialProps } from '@/app/layout.client';

export interface IAtClickableProps {
  children?: ReactNode;
  className?: string;
  href?: string;
  icon?: EIcons;
  iconPosition?: TClickableIconPosition;
  label?: string;
  outline?: boolean;
  size?: EClickableSize;
  target?: EClickableTarget;
  variant?: TClickableVariants;
  type?: EClickableTypes;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  ariaLabel?: string;
  rel?: string;
  gtmLabel?: string;
  download?: boolean;
  'data-testid'?: string;
  buttonType?: 'button' | 'submit' | 'reset';
  interactLocation: string;
  shouldSendGTMEvent?: boolean;
  customGTMClick?: (e: MouseEvent<HTMLElement>) => void;
}

/**
 * `AtClickable` is a flexible, polymorphic component that conditionally renders
 * a `<button>` or a Next.js `<Link>`, depending on the props passed.
 *
 * It supports internal/external links, modal actions, GTM tracking, and styling variants.
 * Commonly used as the core logic behind `AtButton` and `AtLink`.
 *
 * Context-aware behaviors include login modals, Calendly links, region redirects, and lead gen flows.
 *
 */

export const AtClickable = ({
  children,
  className,
  href,
  iconPosition = 'none',
  label,
  outline,
  size = EClickableSize.LG,
  type = EClickableTypes.CTA,
  target,
  variant,
  onClick,
  ariaLabel,
  rel,
  download = false,
  buttonType = 'button',
  gtmLabel,
  'data-testid': dataTestId = 'at-clickable',
  interactLocation,
  shouldSendGTMEvent = true,
  customGTMClick,
}: IAtClickableProps): JSX.Element => {
  const { studio3DData } = useInitialProps();
  const { currentBuildStudio } = useLocation();
  const { buildLoginUrl } = useContext(LoginModalContext);
  const { openLeadGenModal } = useLeadGenModal();
  const { handleOpen } = useSetLocationModal();
  const [calendlyLink, setCalendlyLink] = useState<string>();

  const resolvedVariant =
    type === EClickableTypes.LINK &&
    ![EClickableVariants.PRIMARY, EClickableVariants.SECONDARY].includes(
      variant!,
    )
      ? EClickableVariants.PRIMARY
      : variant;

  useEffect(() => {
    const fetchCalendly = async () => {
      if (currentBuildStudio?.Id) {
        const result = await getDesignStudioCalendlyLink(
          currentBuildStudio.Id,
          studio3DData,
        );
        setCalendlyLink(result);
      }
    };

    fetchCalendly();
  }, [currentBuildStudio?.Id, studio3DData]);

  const iconPos: TClickableIconPosition =
    target === EClickableTarget.BLANK ? 'right' : iconPosition;

  const accessibleLabel =
    target === EClickableTarget.BLANK
      ? `${ariaLabel}, opens in new tab`
      : ariaLabel;

  const isLeadGenModal =
    href?.startsWith(':openLeadGen') ||
    (href?.startsWith(':calendly') && !calendlyLink);

  const isLoginFlow = href?.startsWith(':openLoginModal');

  const classes = atClickable({
    intent: resolvedVariant,
    outline,
    size,
    type,
    iconPosition: iconPos,
  });

  const hrefQueryParams = (() => {
    if (href?.includes('?')) {
      const queryString = href.split('?')[1];
      return new URLSearchParams(queryString);
    }
    return new URLSearchParams();
  })();

  const currentHref = (() => {
    if (href?.startsWith(':includedFeatures')) {
      return getRegionUrlById(currentBuildStudio?.Id);
    }

    if (isLoginFlow) {
      return buildLoginUrl({
        StudioId:
          currentBuildStudio?.Id?.toString() ||
          hrefQueryParams?.get('StudioId') ||
          undefined,
        PlanId: hrefQueryParams?.get('PlanId') || undefined,
        ElevationId: hrefQueryParams?.get('ElevationId') || undefined,
        LoginAction: hrefQueryParams?.get('LoginAction') || undefined,
      });
    }

    return href ?? '';
  })();

  const fireGTMClick = () =>
    sendGTMClickEvent(
      gtmLabel || label || ariaLabel || '',
      interactLocation,
      href || 'not_available',
    );

  const fireGTMNav = (url: string, e: MouseEvent<HTMLElement>) => {
    if (shouldSendGTMEvent) {
      sendGTMNavigationEvent(
        gtmLabel || label || ariaLabel || '',
        interactLocation,
        url,
      );
    } else if (customGTMClick) {
      customGTMClick(e);
    }
  };

  const handleClick = (
    e: MouseEvent<HTMLButtonElement>,
    action?: 'chat' | 'modal' | 'location',
  ) => {
    if (shouldSendGTMEvent) fireGTMClick();

    switch (action) {
      case 'chat':
        openChat(interactLocation);
        break;
      case 'modal':
        if (href) openLeadGenModal(href);
        break;
      case 'location':
        const action = hrefQueryParams?.get('LoginAction');
        handleOpen({
          type:
            action == 'LOT_VISION'
              ? ESetLocationType.LOT_VISION
              : ESetLocationType.LOGIN,
          redirect: currentHref,
        });
        break;
      default:
        onClick?.(e);
    }
  };

  const renderButton = (action?: 'chat' | 'modal' | 'location') => (
    <button
      onClick={(e) => handleClick(e, action)}
      className={classNames(classes.base(), className)}
      aria-label={ariaLabel}
      type={buttonType}
      data-testid={dataTestId}
    >
      <span
        className={classNames(
          'flex items-center',
          classes.iconWrapper({ iconPosition }),
        )}
      >
        {label}
        {children}
      </span>
    </button>
  );

  const renderLink = (url?: string) => (
    <Link
      href={url || currentHref}
      className={classNames(classes.base(), className, 'inline-block')}
      target={target}
      rel={rel}
      aria-label={accessibleLabel}
      data-testid={dataTestId}
      download={download}
      onClick={(e) => fireGTMNav(url || currentHref, e)}
    >
      <span
        className={classNames(
          'flex items-center gap-2',
          classes.iconWrapper({ iconPosition }),
        )}
      >
        {label}
        {children}
        {target === EClickableTarget.BLANK && !children && (
          <AtIcon
            icon={EIcons.LEAVE}
            color={classes.iconColor({ intent: variant, outline, type })}
          />
        )}
      </span>
    </Link>
  );

  // Main Render Logic
  if (isLoginFlow && !currentBuildStudio?.Id) return renderButton('location');
  if (href === ':openChat') return renderButton('chat');
  if (isLeadGenModal) return renderButton('modal');
  if (href?.startsWith(':calendly') && calendlyLink)
    return renderLink(calendlyLink);
  if (href) return renderLink();

  return renderButton();
};
